import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { SpinnerComponent } from '@/components/shared/spinner/spinner.component';
import { ClarificationService } from '../clarification-request.service';
import { ClarificationRepository } from '@/store/clarification-request-state/clarification-request-state.repository';
import { ClarifcationMetadataBlocksService } from '../clarifaction-metadata-blocks.service';
import { getOutputDetailsFormObject } from '../output-details/utils/output-details-form-object';
import { getClarifcationRequestPVItems } from '../output-details/utils/clarification-request-pv-items';
import { DemandeWithTaskDTO } from '@/types';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { StatusComponent } from '@/components/shared/status/status.component';

@Component({
  selector: 'sm-clarification-inspect-content-block',
  standalone: true,
  imports: [CommonModule, FormBuilderComponent, BlockBuilderComponent, SpinnerComponent, TranslateModule, StatusComponent],
  template: `
    <sm-spinner [loading]="loading$"></sm-spinner>
    <div class="flex flex-row gap-5" *ngIf="!(loading$ | async) && formObject">
      <div class="flex flex-col gap-4 w-3/4">
        <div class="content" *ngIf="metadataBlocks.length > 0">
          <div class="h-fit flex flex-col gap-4" *ngFor="let blocks of metadataBlocks">
            <sm-block-builder [blocks]="blocks"></sm-block-builder>
          </div>
        </div>
        <div class="content">
          <sm-form-builder [formObject]="formObject"></sm-form-builder>
        </div>
      </div>

      <div class="flex flex-col gap-2 w-1/4">
        <sm-status [status]="'clarification.status.' + status" [extra]="taskName"></sm-status>
      </div>
    </div>
  `,
  styles: [`
    .content {
      @apply bg-white p-6 rounded-xl shadow-sm border border-gray-100;
    }
  `]
})
export class ClarificationInspectContentBlockComponent implements OnInit {
  private clarificationService = inject(ClarificationService);
  private clarificationRepository = inject(ClarificationRepository);
  private metadataBlocksService = inject(ClarifcationMetadataBlocksService);
  private translate = inject(TranslateService);

  private _id?: number;
  @Input() set id(value: number | undefined) {
    this._id = value;
    this.tryLoadData();
  }
  get id(): number | undefined {
    return this._id;
  }

  private _version?: number;
  @Input() set version(value: number | undefined) {
    this._version = value;
    this.tryLoadData();
  }
  get version(): number | undefined {
    return this._version;
  }

  loading$ = new BehaviorSubject<boolean>(true);
  formObject?: DynamicForm;
  metadataBlocks: DynamicBlocks[] = [];
  status = '';
  taskName = '';

  ngOnInit(): void {
    this.tryLoadData();
  }

  private tryLoadData() {
    if (this._id) {
      this.loadData();
    }
  }

  loadData() {
    this.loading$.next(true);
    this.clarificationService.getDemandeWithTask(this._id!).subscribe({
      next: (data: DemandeWithTaskDTO) => {
        this.status = data.demande.status;
        this.taskName = data.taskName || '';
        this.setupDetails(data);
        this.loading$.next(false);
      },
      error: () => {
        this.loading$.next(false);
      }
    });
  }

  setupDetails(data: DemandeWithTaskDTO) {
    this.formObject = getOutputDetailsFormObject(this.clarificationRepository, data);

    this.metadataBlocks = this.metadataBlocksService.getMetadataBlocks({
      pvItems: getClarifcationRequestPVItems(data),
      clarificationOpinions: data.avis
    });
  }
}
